import {BranchName, NavList} from '@primer/react'
import {PlusIcon, DotIcon} from '@primer/octicons-react'
import {CommandKeybindingHint} from '@github-ui/ui-commands'
import type {Ref} from 'react'
import type {StackData} from '../page-data/payloads/header'
import {MAX_STACK_SIZE} from '../helpers/constants'
import {StateIcon} from './StateIcon'
import styles from './StackState.module.css'

type StackOverlayProps = {
  pullRequestNumber: number
  stack: StackData
  baseBranchText: string
  addToStackURL?: string
  showShortcutGuidance: boolean
  tabSuffix?: string
  showStackMutationButtons?: boolean
  // Attached to the current entry so the overlay's focus trap focuses it on open,
  // which scrolls it into view within a long stack.
  currentEntryRef?: Ref<HTMLAnchorElement>
  onSwitchBranch?: (targetPullRequestId: number, targetPositionInStack: number) => void
  onAddToStackClick?: () => void
}

export function StackOverlay({
  pullRequestNumber,
  stack,
  baseBranchText,
  addToStackURL,
  showShortcutGuidance,
  tabSuffix = '',
  showStackMutationButtons = true,
  currentEntryRef,
  onSwitchBranch,
  onAddToStackClick,
}: StackOverlayProps) {
  const {pulls, allPullsLocked} = stack
  const isStackAtCapacity = stack.size === MAX_STACK_SIZE
  const shouldShowStackMutationButtons = showStackMutationButtons && addToStackURL && !allPullsLocked

  return (
    <>
      <NavList className={styles.stackNavList} disableItemGap>
        {shouldShowStackMutationButtons && (
          <NavList.Item
            className={styles.addToStackItem}
            href={addToStackURL}
            inactiveText={isStackAtCapacity ? 'Maximum stack size reached' : undefined}
            onClick={() => {
              onAddToStackClick?.()
            }}
          >
            <NavList.LeadingVisual>
              <PlusIcon />
            </NavList.LeadingVisual>
            Add to stack
          </NavList.Item>
        )}
        {pulls.map((stackedPR, index) => {
          const isCurrent = stackedPR.number === pullRequestNumber
          return (
            <NavList.Item
              key={`stacked-pr-${stackedPR.number}`}
              ref={isCurrent ? currentEntryRef : undefined}
              href={`${stackedPR.url}${tabSuffix}`}
              aria-current={isCurrent ? 'page' : undefined}
              onClick={() => {
                if (isCurrent) return
                onSwitchBranch?.(stackedPR.number, index + 1)
              }}
            >
              <NavList.LeadingVisual>
                <StateIcon state={stackedPR.state} />
              </NavList.LeadingVisual>
              {stackedPR.title}
              <NavList.Description variant="block">
                #{stackedPR.number} · {stackedPR.headBranch}
              </NavList.Description>
            </NavList.Item>
          )
        })}
        <li className={styles.branchItem}>
          <span className={styles.branchItemContent}>
            <DotIcon className="fgColor-muted" />
            <BranchName as="span">{baseBranchText}</BranchName>
          </span>
        </li>
      </NavList>
      {showShortcutGuidance && (
        <div className={styles.previewFooter}>
          <span className={styles.shortcutGuidance}>
            Use <CommandKeybindingHint commandId="pull-requests:navigate-downstack" />{' '}
            <CommandKeybindingHint commandId="pull-requests:navigate-upstack" /> to navigate down/up the stack
          </span>
        </div>
      )}
    </>
  )
}
